/**
 * Maintenance.js
 * Pagina di manutenzione (solo admin): lista utenti con ruolo athlete/parent
 * e pulsante per impersonificare l'accesso al portale atleta.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Badge,
  Alert,
} from 'react-bootstrap';
import { maintenanceService } from '../../services/api';

const ROLE_LABELS = {
  athlete: { label: 'Atleta', bg: 'primary' },
  parent: { label: 'Genitore', bg: 'info' },
};

const Maintenance = () => {
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [impersonateError, setImpersonateError] = useState(null);

  const LIMIT = 20;

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await maintenanceService.getPortalUsers({
      search,
      role: roleFilter || undefined,
      page,
      limit: LIMIT,
    });
    if (result.success) {
      setUsers(result.data.items || []);
      setTotalCount(result.data.totalCount || 0);
    } else {
      setError(result.message || 'Errore nel caricamento degli utenti');
    }
    setLoading(false);
  }, [search, roleFilter, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, roleFilter]);

  const handleImpersonate = async (user) => {
    setImpersonatingId(user.id);
    setImpersonateError(null);
    const result = await maintenanceService.impersonateUser(user.id);
    if (result.success) {
      const token = result.data.token;
      const url = `${window.location.origin}/portal/impersonate/${token}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      setImpersonateError(result.message || "Errore nell'avvio dell'impersonificazione");
    }
    setImpersonatingId(null);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const getRoleBadges = (roles) => {
    if (!roles || roles.length === 0) return <span className="text-muted">—</span>;
    return roles
      .filter((r) => ROLE_LABELS[r])
      .map((r) => (
        <Badge key={r} bg={ROLE_LABELS[r].bg} className="me-1">
          {ROLE_LABELS[r].label}
        </Badge>
      ));
  };

  const totalPages = Math.ceil(totalCount / LIMIT);

  return (
    <Container fluid>
      <Row className="mb-4 align-items-center">
        <Col>
          <h2 className="mb-0">
            <i className="fas fa-tools me-2 text-secondary"></i>
            Manutenzione
          </h2>
          <p className="text-muted mb-0 mt-1">
            Impersonifica l'accesso al portale atleta per assistere gli utenti.
          </p>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={6}>
              <Form.Label className="fw-medium">Cerca</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <i className="fas fa-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Cerca per nome o cognome..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="search-input"
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-medium">Ruolo</Form.Label>
              <Form.Select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                data-testid="role-filter"
              >
                <option value="">Tutti (Atleta + Genitore)</option>
                <option value="athlete">Solo Atleti</option>
                <option value="parent">Solo Genitori</option>
              </Form.Select>
            </Col>
            <Col md={3} className="text-md-end">
              <span className="text-muted small">
                {loading ? '...' : `${totalCount} utenti trovati`}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Errors */}
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {impersonateError && (
        <Alert
          variant="warning"
          onClose={() => setImpersonateError(null)}
          dismissible
          data-testid="impersonate-error"
        >
          <i className="fas fa-exclamation-triangle me-2"></i>
          {impersonateError}
        </Alert>
      )}

      {/* Table */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Caricamento utenti...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-5 text-muted" data-testid="empty-state">
              <i className="fas fa-users fs-1 mb-3 d-block opacity-25"></i>
              <p>Nessun utente trovato</p>
            </div>
          ) : (
            <Table hover responsive className="mb-0" data-testid="users-table">
              <thead className="table-light">
                <tr>
                  <th>Nome</th>
                  <th>Cognome</th>
                  <th>Data di Nascita</th>
                  <th>Ruolo</th>
                  <th className="text-end">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td className="align-middle">{user.firstName}</td>
                    <td className="align-middle">{user.lastName}</td>
                    <td className="align-middle">{formatDate(user.dateOfBirth)}</td>
                    <td className="align-middle">{getRoleBadges(user.roles)}</td>
                    <td className="align-middle text-end">
                      <Button
                        variant="outline-purple"
                        size="sm"
                        className="d-inline-flex align-items-center gap-1"
                        style={{ borderColor: '#6f42c1', color: '#6f42c1' }}
                        onClick={() => handleImpersonate(user)}
                        disabled={impersonatingId === user.id}
                        data-testid={`impersonate-btn-${user.id}`}
                        title={`Accedi al portale come ${user.firstName} ${user.lastName}`}
                      >
                        {impersonatingId === user.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i className="fas fa-user-secret"></i>
                        )}
                        <span>Impersonifica</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>

        {/* Pagination */}
        {totalPages > 1 && (
          <Card.Footer className="d-flex justify-content-between align-items-center">
            <span className="text-muted small">
              Pagina {page} di {totalPages}
            </span>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                data-testid="prev-page"
              >
                <i className="fas fa-chevron-left"></i>
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                data-testid="next-page"
              >
                <i className="fas fa-chevron-right"></i>
              </Button>
            </div>
          </Card.Footer>
        )}
      </Card>
    </Container>
  );
};

export default Maintenance;
